/**
 * Phase 9: Priority Date Intelligence Panel
 *
 * Inline tab component inside DocumentPanel for employment-based pathways.
 * Shows:
 *  - Current Visa Bulletin cutoff for user's country + EB category
 *  - Personalised wait-time estimate (if priority date is set)
 *  - Historical trend chart (last 12 months, ASCII-style bars)
 *  - All-countries comparison table for current bulletin month
 *  - Pathway switch recommendation cards with CTA
 */

import React, { useState, useEffect, useCallback } from 'react';
import { API_BASE as API } from '../lib/apiBase';

const apiFetch = async (path, options = {}) => {
  const token = localStorage.getItem('token');
  const res = await fetch(`${API}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {}),
    },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const msg = data?.detail?.message || data?.detail || 'Request failed';
    throw Object.assign(new Error(msg), { status: res.status, data });
  }
  return data;
};

// ---------------------------------------------------------------------------
// Small helpers
// ---------------------------------------------------------------------------

const fmt = (isoDate) => {
  if (!isoDate) return 'Current (C)';
  try {
    return new Date(isoDate).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return isoDate;
  }
};

const fmtMonth = (isoMonth) => {
  if (!isoMonth) return '—';
  try {
    const [y, m] = isoMonth.split('-');
    const d = new Date(parseInt(y), parseInt(m) - 1, 1);
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
  } catch {
    return isoMonth;
  }
};

const waitLabel = (months) => {
  if (months === null || months === undefined) return null;
  if (months === 0) return 'Your date is current — you may be eligible to file now!';
  if (months < 12) return `~${months} month${months !== 1 ? 's' : ''}`;
  const y = Math.floor(months / 12);
  const m = months % 12;
  return m > 0 ? `~${y} yr ${m} mo` : `~${y} yr${y !== 1 ? 's' : ''}`;
};

const EB_CATEGORY_LABELS = {
  'EB-1': 'EB-1 (Extraordinary Ability / Priority Workers)',
  'EB-2': 'EB-2 (Advanced Degree / Exceptional Ability + NIW)',
  'EB-3': 'EB-3 (Skilled Workers / Professionals)',
};

// ---------------------------------------------------------------------------
// Trend bar chart
// ---------------------------------------------------------------------------
const TrendChart = ({ trend }) => {
  if (!trend || trend.length === 0) {
    return <p className="text-gray-400 text-sm italic">No historical data available yet.</p>;
  }

  // Collect all non-null dates to determine range
  const dates = trend
    .map((t) => t.final_action_date)
    .filter(Boolean)
    .map((d) => new Date(d).getTime());

  const hasCurrentEntries = trend.some((t) => t.is_current);
  const minTs = dates.length ? Math.min(...dates) : 0;
  const maxTs = dates.length ? Math.max(...dates) : 0;
  const range = maxTs - minTs || 1;

  return (
    <div>
      <p className="text-xs text-gray-500 mb-3">
        Final Action Date movement over the last {trend.length} bulletin months
        {hasCurrentEntries && ' (some months show "Current")'}
      </p>
      <div className="space-y-1">
        {trend.map((point, idx) => {
          const isCurr = point.is_current;
          const ts = point.final_action_date ? new Date(point.final_action_date).getTime() : null;
          const pct = ts ? Math.max(8, Math.round(((ts - minTs) / range) * 100)) : 100;

          return (
            <div key={idx} className="flex items-center gap-2 text-xs">
              <span className="text-gray-500 w-16 shrink-0 text-right">
                {fmtMonth(point.bulletin_month).replace(/\s\d{4}/, '')} {/* month only */}
                {' '}{(point.bulletin_month || '').slice(0, 4)}
              </span>
              <div className="flex-1 bg-gray-100 rounded h-5 relative overflow-hidden">
                <div
                  className={`h-full rounded transition-all ${isCurr ? 'bg-green-400' : 'bg-blue-400'}`}
                  style={{ width: `${isCurr ? 100 : pct}%` }}
                />
                <span className="absolute inset-0 flex items-center px-2 text-white text-xs font-medium">
                  {isCurr ? 'Current (C)' : fmt(point.final_action_date)}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// All-countries table
// ---------------------------------------------------------------------------
const AllCountriesTable = ({ entries, userCountry }) => {
  if (!entries || entries.length === 0) return null;
  return (
    <table className="w-full text-sm border-collapse">
      <thead>
        <tr className="bg-gray-50">
          <th className="text-left p-2 border border-gray-200 font-semibold">Country of Chargeability</th>
          <th className="text-left p-2 border border-gray-200 font-semibold">Final Action Date</th>
          <th className="text-left p-2 border border-gray-200 font-semibold">Dates for Filing</th>
        </tr>
      </thead>
      <tbody>
        {entries.map((e, idx) => {
          const isYou = userCountry && e.country_of_chargeability === userCountry.toUpperCase();
          return (
            <tr
              key={idx}
              className={isYou ? 'bg-blue-50 font-semibold' : idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}
            >
              <td className="p-2 border border-gray-200">
                {e.country_of_chargeability}
                {isYou && (
                  <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-1 rounded">You</span>
                )}
              </td>
              <td className="p-2 border border-gray-200">
                <span className={e.is_current ? 'text-green-700 font-medium' : 'text-gray-800'}>
                  {e.is_current ? '✅ Current' : fmt(e.final_action_date)}
                </span>
              </td>
              <td className="p-2 border border-gray-200">
                {e.filing_date ? fmt(e.filing_date) : <span className="text-gray-400">—</span>}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

// ---------------------------------------------------------------------------
// Switch recommendation card
// ---------------------------------------------------------------------------
const SwitchCard = ({ rec, pathwayId, onDismiss, onSwitch }) => {
  const [switching, setSwitching] = useState(false);
  const [dismissing, setDismissing] = useState(false);

  const handleSwitch = async () => {
    setSwitching(true);
    try {
      await onSwitch(rec);
    } finally {
      setSwitching(false);
    }
  };

  const handleDismiss = async () => {
    setDismissing(true);
    try {
      await onDismiss(rec.id);
    } finally {
      setDismissing(false);
    }
  };

  return (
    <div className="border border-amber-200 bg-amber-50 rounded-lg p-4">
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-amber-600 font-bold text-sm">💡 Switch Recommendation</span>
          </div>
          <h4 className="font-semibold text-gray-800 text-sm">
            {rec.current_pathway_name} → {rec.recommended_pathway_name}
          </h4>
          {rec.estimated_time_difference_label && (
            <p className="text-green-700 text-sm font-medium mt-1">
              ⚡ {rec.estimated_time_difference_label}
            </p>
          )}
          <p className="text-gray-600 text-sm mt-2">{rec.recommendation_reason}</p>

          {rec.evidence_requirements && rec.evidence_requirements.length > 0 && (
            <div className="mt-3">
              <p className="text-xs font-semibold text-gray-700 mb-1">Evidence you'll need:</p>
              <ul className="text-xs text-gray-600 list-disc list-inside space-y-0.5">
                {rec.evidence_requirements.slice(0, 3).map((r, i) => (
                  <li key={i}>{r}</li>
                ))}
                {rec.evidence_requirements.length > 3 && (
                  <li className="text-gray-400">
                    +{rec.evidence_requirements.length - 3} more criteria
                  </li>
                )}
              </ul>
            </div>
          )}

          <div className="mt-3 flex items-center gap-3">
            <div className="text-xs text-gray-500">
              Switch price:{' '}
              <span className="text-green-700 font-bold text-sm">
                ${rec.switch_price?.toFixed(0)}
              </span>
              {rec.full_price && (
                <span className="ml-1 line-through text-gray-400">${rec.full_price?.toFixed(0)}</span>
              )}
              <span className="ml-1 text-green-600 text-xs">(50% off)</span>
            </div>
          </div>
        </div>
      </div>
      <div className="mt-3 flex gap-2">
        <button
          onClick={handleSwitch}
          disabled={switching}
          className="flex-1 bg-amber-500 hover:bg-amber-600 disabled:bg-amber-300 text-white text-sm font-medium px-3 py-2 rounded transition-colors"
        >
          {switching ? 'Redirecting…' : `Switch for $${rec.switch_price?.toFixed(0)}`}
        </button>
        <button
          onClick={handleDismiss}
          disabled={dismissing}
          className="px-3 py-2 text-sm text-gray-500 hover:text-gray-700 border border-gray-300 rounded transition-colors"
        >
          {dismissing ? '…' : 'Dismiss'}
        </button>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
const PriorityDatePanel = ({ pathwayId }) => {
  const [dashboard, setDashboard] = useState(null);
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [priorityDateInput, setPriorityDateInput] = useState('');
  const [savingPD, setSavingPD] = useState(false);
  const [pdSaved, setPdSaved] = useState(false);
  const [activeSection, setActiveSection] = useState('dashboard'); // 'dashboard' | 'trend' | 'countries' | 'switch'
  const [generatingRecs, setGeneratingRecs] = useState(false);

  const loadDashboard = useCallback(async (customPD) => {
    setLoading(true);
    setError(null);
    try {
      const qs = customPD ? `?priority_date=${encodeURIComponent(customPD)}` : '';
      const data = await apiFetch(`/api/priority-date/${pathwayId}${qs}`);
      setDashboard(data);
      if (data.your_priority_date) {
        setPriorityDateInput(data.your_priority_date.slice(0, 10));
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [pathwayId]);

  const loadRecommendations = useCallback(async () => {
    try {
      const data = await apiFetch('/api/priority-date/switch-recommendations');
      setRecommendations(data.recommendations || []);
    } catch {
      // non-critical — don't surface error for recommendations
    }
  }, []);

  useEffect(() => {
    loadDashboard();
    loadRecommendations();
  }, [loadDashboard, loadRecommendations]);

  const handleSavePriorityDate = async () => {
    if (!priorityDateInput) return;
    setSavingPD(true);
    try {
      await apiFetch(`/api/priority-date/${pathwayId}/priority-date`, {
        method: 'POST',
        body: JSON.stringify({ priority_date: priorityDateInput }),
      });
      setPdSaved(true);
      setTimeout(() => setPdSaved(false), 3000);
      await loadDashboard(priorityDateInput);
    } catch (e) {
      setError(e.message);
    } finally {
      setSavingPD(false);
    }
  };

  const handleDismissRec = async (recId) => {
    try {
      await apiFetch(`/api/priority-date/switch-recommendations/${recId}/dismiss`, {
        method: 'POST',
      });
      setRecommendations((prev) => prev.filter((r) => r.id !== recId));
    } catch (e) {
      setError(e.message);
    }
  };

  const handleSwitchRec = async (rec) => {
    try {
      const data = await apiFetch('/api/priority-date/switch-checkout', {
        method: 'POST',
        body: JSON.stringify({
          source_pathway_id: rec.current_pathway_id,
          destination_pathway_id: rec.recommended_pathway_id,
          recommendation_id: rec.id,
        }),
      });
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (e) {
      setError(e.message);
    }
  };

  const handleGenerateRecs = async () => {
    setGeneratingRecs(true);
    try {
      await apiFetch('/api/priority-date/switch-recommendations/generate', { method: 'POST' });
      await loadRecommendations();
    } catch (e) {
      setError(e.message);
    } finally {
      setGeneratingRecs(false);
    }
  };

  // ---- Render ----

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-3" />
          <p className="text-gray-500 text-sm">Loading Priority Date Intelligence…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
        <p className="text-red-700 font-medium">⚠️ {error}</p>
        <button
          onClick={() => loadDashboard()}
          className="mt-2 text-sm text-red-600 underline hover:no-underline"
        >
          Try again
        </button>
      </div>
    );
  }

  if (!dashboard) return null;

  const {
    pathway_name,
    eb_category,
    current_bulletin_month,
    your_country,
    final_action_date,
    is_current,
    your_priority_date,
    estimated_wait_months,
    projected_availability,
    trend,
    all_countries_data,
  } = dashboard;

  const tabs = [
    { id: 'dashboard', label: '📊 Dashboard' },
    { id: 'trend', label: '📈 Trend' },
    { id: 'countries', label: '🌍 All Countries' },
    { id: 'switch', label: `💡 Switch${recommendations.length > 0 ? ` (${recommendations.length})` : ''}` },
  ];

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg p-4">
        <h3 className="font-bold text-lg">Priority Date Intelligence</h3>
        <p className="text-blue-100 text-sm">
          {pathway_name} · {EB_CATEGORY_LABELS[eb_category] || eb_category}
        </p>
        {current_bulletin_month && (
          <p className="text-blue-200 text-xs mt-1">
            Visa Bulletin: {fmtMonth(current_bulletin_month)}
          </p>
        )}
      </div>

      {/* Current status card */}
      <div className={`rounded-lg p-4 border-2 ${is_current ? 'border-green-400 bg-green-50' : 'border-blue-200 bg-blue-50'}`}>
        <div className="flex flex-wrap gap-4 justify-between items-start">
          <div>
            <p className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-1">
              Final Action Date — {your_country || 'Your Country'}
            </p>
            <p className={`text-2xl font-bold ${is_current ? 'text-green-700' : 'text-blue-800'}`}>
              {is_current ? '✅ Current' : fmt(final_action_date)}
            </p>
            {is_current && (
              <p className="text-green-600 text-sm mt-1">
                You may be eligible to file or adjust status right now.
              </p>
            )}
          </div>
          {!is_current && estimated_wait_months !== null && estimated_wait_months !== undefined && (
            <div className="text-right">
              <p className="text-xs text-gray-500 mb-1 uppercase tracking-wide">Est. Wait</p>
              <p className="text-xl font-bold text-orange-600">{waitLabel(estimated_wait_months)}</p>
              {projected_availability && projected_availability !== 'Current' && (
                <p className="text-xs text-gray-500 mt-1">
                  Available ~{fmtMonth(projected_availability)}
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Priority date input */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
        <p className="text-xs font-medium text-gray-700 mb-2">
          Your Priority Date (for personalised wait estimate)
        </p>
        <div className="flex gap-2">
          <input
            type="date"
            value={priorityDateInput}
            onChange={(e) => setPriorityDateInput(e.target.value)}
            className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
          />
          <button
            onClick={handleSavePriorityDate}
            disabled={savingPD || !priorityDateInput}
            className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white text-sm px-3 py-1 rounded transition-colors"
          >
            {savingPD ? '…' : pdSaved ? '✓ Saved' : 'Save'}
          </button>
        </div>
        <p className="text-xs text-gray-400 mt-1">
          This is the date your labor certification or I-140 petition was filed.
        </p>
      </div>

      {/* Section tabs */}
      <div className="border-b border-gray-200 flex gap-1 flex-wrap">
        {tabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setActiveSection(t.id)}
            className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeSection === t.id
                ? 'border-blue-600 text-blue-700'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Section content */}
      {activeSection === 'dashboard' && (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-white border border-gray-200 rounded-lg p-3">
              <p className="text-xs text-gray-500 mb-1">EB Category</p>
              <p className="font-semibold text-gray-800">{eb_category}</p>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-3">
              <p className="text-xs text-gray-500 mb-1">Country of Chargeability</p>
              <p className="font-semibold text-gray-800">{your_country || '—'}</p>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-3">
              <p className="text-xs text-gray-500 mb-1">Current Cutoff</p>
              <p className={`font-semibold ${is_current ? 'text-green-700' : 'text-gray-800'}`}>
                {is_current ? 'Current ✅' : fmt(final_action_date)}
              </p>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-3">
              <p className="text-xs text-gray-500 mb-1">Your Priority Date</p>
              <p className="font-semibold text-gray-800">
                {your_priority_date ? fmt(your_priority_date) : <span className="text-gray-400 text-sm">Not set</span>}
              </p>
            </div>
          </div>

          {!is_current && estimated_wait_months !== null && estimated_wait_months !== undefined && (
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
              <p className="text-sm font-medium text-orange-800">
                ⏳ Estimated wait: <strong>{waitLabel(estimated_wait_months)}</strong>
              </p>
              {projected_availability && projected_availability !== 'Current' && (
                <p className="text-sm text-orange-600 mt-1">
                  Your priority date may become current around{' '}
                  <strong>{fmtMonth(projected_availability)}</strong>.
                </p>
              )}
              <p className="text-xs text-orange-500 mt-2">
                * Estimate based on historical Visa Bulletin advancement. Actual dates are subject to change monthly.
              </p>
            </div>
          )}

          <p className="text-xs text-gray-400 italic">
            Disclaimer: This is for informational purposes only and does not constitute legal advice.
            Always verify with the official USCIS Visa Bulletin before making filing decisions.
          </p>
        </div>
      )}

      {activeSection === 'trend' && (
        <div>
          <TrendChart trend={trend} />
        </div>
      )}

      {activeSection === 'countries' && (
        <div>
          <p className="text-sm text-gray-600 mb-3">
            Final Action Dates for <strong>{eb_category}</strong> — {fmtMonth(current_bulletin_month)}
          </p>
          <AllCountriesTable entries={all_countries_data} userCountry={your_country} />
        </div>
      )}

      {activeSection === 'switch' && (
        <div className="space-y-3">
          {recommendations.length === 0 ? (
            <div className="text-center py-6">
              <p className="text-gray-500 text-sm mb-3">
                No switch recommendations at this time.
              </p>
              <button
                onClick={handleGenerateRecs}
                disabled={generatingRecs}
                className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white text-sm px-4 py-2 rounded transition-colors"
              >
                {generatingRecs ? 'Analysing…' : '🔍 Check for Recommendations'}
              </button>
              <p className="text-xs text-gray-400 mt-2">
                We'll evaluate your profile and active pathways for faster alternatives.
              </p>
            </div>
          ) : (
            <>
              <p className="text-sm text-gray-600">
                Based on your country of birth and active pathways, we found{' '}
                <strong>{recommendations.length}</strong> potential faster pathway
                {recommendations.length !== 1 ? 's' : ''}:
              </p>
              {recommendations.map((rec) => (
                <SwitchCard
                  key={rec.id}
                  rec={rec}
                  pathwayId={pathwayId}
                  onDismiss={handleDismissRec}
                  onSwitch={handleSwitchRec}
                />
              ))}
              <button
                onClick={handleGenerateRecs}
                disabled={generatingRecs}
                className="w-full border border-gray-300 text-gray-600 text-sm py-2 rounded hover:bg-gray-50 transition-colors"
              >
                {generatingRecs ? 'Refreshing…' : '↻ Refresh Recommendations'}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default PriorityDatePanel;
